import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  ViewChild,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { OrderTableDTO } from '@app/api/order-service/model/orderTableDTO';
import {
  FormField,
  TableSection,
  SummarySection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { GoodsShopControllerService } from '@app/api/goods-service/api/goodsShopController.service';
import { GoodsSkuDetailVO } from '@app/api/goods-service/model/goodsSkuDetailVO';
import { HttpResponse } from '@angular/common/http';
import { ProductSelectionDialogComponent, ProductItem } from '../components/product-selection-dialog/product-selection-dialog.component';
import { ProductService, ProductSearchParams } from '../services/internal.service';
import { PRODUCT_SERVICE_CONFIG, ORDER_CREATE_FORM_CONFIG } from '../internal.config';
import { OrderCreateLayoutComponent } from '@shared/components/order-create-layout/order-create-layout.component';
import { ProvinceService } from '@app/feature/center-business/channel/service/province.service';
import { O2OOrderService } from '@app/feature/center-business/o2o/services/o2o-order.service';
import { O2O_TAKING_DELIVERY_WAY } from '@app/feature/center-business/o2o/services/o2o.config';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-order-create',
  standalone: true,
  imports: [OrderCreateLayoutComponent, TranslateModule, ProductSelectionDialogComponent, ToastModule],
  templateUrl: './order-create.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class OrderCreateComponent implements OnInit, CanComponentDeactivate {
  private router = inject(Router);
  private orderService = inject(OrderControllerService);
  private o2oOrderService = inject(O2OOrderService);
  private productService = inject(ProductService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);
  private provinceService = inject(ProvinceService);
  loading = false;
  error: string | null = null;
  private skipCanDeactivate = false;
  availableSkus: GoodsSkuDetailVO[] = [];
  private previousStoreId: number | undefined;

  // Product selection dialog state
  showProductSelectionDialog = false;
  productDialogProducts: ProductItem[] = [];
  productDialogLoading = false;
  productDialogTotalRecords = 0;
  productDialogCurrentPage = 1;
  productDialogPageSize = PRODUCT_SERVICE_CONFIG.defaultPageSize;
  
  // Track already selected products for disabling in dialog
  private alreadySelectedSkuCodes = new Set<string>();

  // Header Actions
  headerActions: ActionButton[] = [
    { label: 'common.actions.cancel', action: 'cancel', outlined: true, severity: 'secondary' },
    { label: 'common.actions.save', action: 'save', severity: 'info' }
  ];

  formFields: FormField[] = [];

  // Table Sections for Product Information (clone — never mutate shared config)
  tableSections: TableSection[] = ORDER_CREATE_FORM_CONFIG.tableSections.map(
    (section) => ({
      ...section,
      data: [...(section.data || [])],
      selection: [...(section.selection || [])],
      actions: section.actions?.map((action) => ({ ...action })),
      columns: section.columns?.map((column) => ({ ...column })),
    }),
  );

  // Summary Section
  summarySection: SummarySection = {
    ...ORDER_CREATE_FORM_CONFIG.summarySection,
    items: ORDER_CREATE_FORM_CONFIG.summarySection.items.map((item) => ({
      ...item,
    })),
  };
  selectedStore:
    | {
        label: string;
        value: number;
        spuId?: number;
        spuCode?: string;
        physicalWarehouseId?: number;
      }
    | undefined;
  provinceOptions = signal<Array<{ label: string; value: number }>>([]);
  // Form data for initialData input
  formData: any = {};
  
  // ViewChild to access the add-form component
  @ViewChild('addForm') addFormComponent!: OrderCreateLayoutComponent;

  ngOnInit(): void {
    this.formFields = this.buildFormFields();
    // Reset component state to ensure clean start
    this.resetComponentState();
    this.setDefaultValues();
    this.updateSummary();
    this.loadProvinceOptions();
    // Ensure selection array is properly initialized
    if (!this.tableSections[0].selection) {
      this.tableSections[0].selection = [];
    }
    // Initialize table actions (disable delete button when no items)
    this.updateTableActions([]);
  }

  private buildFormFields(): FormField[] {
    return withPaginatedFormFields([...ORDER_CREATE_FORM_CONFIG.formFields], {
      storeId: {
        loadPage: (params) =>
          this.o2oOrderService.getSalesStoresPage({
            ...params,
            status: true,
          }),
      },
      provinceId: {
        loadPage: (params) => this.provinceService.getProvinceOptionsPage(params),
      },
    });
  }

  private loadProvinceOptions() {
    this.provinceService.getProvinceOptions().subscribe({
      next: (options) => {
        this.provinceOptions.set(options);
      },
      error: (error) => {
        console.error('Error loading province options:', error);
        this.provinceOptions.set([]);
      },
    });
  }

  private getProvinceName(provinceId: number): string {
    const province = this.provinceOptions().find(p => p.value === provinceId);
    return province ? province.label : '';
  }

  /**
   * Handle store selection change to load available SKUs
   */
  onStoreChange(storeId: number): void {
    this.selectedStore =
      this.o2oOrderService.getSalesStoreById(storeId) ?? undefined;
  }

  /**
   * Load available SKUs for the selected store's SPU
   */
  // private loadAvailableSkus(spuId: number): void {
  //   this.loading = true;
  //   this.error = null;

  //   this.goodsSkuService.goodsSkuDetail(spuId, 'response', false, {
  //     httpHeaderAccept: 'application/json'
  //   } as any)
  //     .subscribe({
  //       next: (response: HttpResponse<GoodsSkuDetailVO[]>) => {
  //         this.availableSkus = response.body || [];
  //         // Convert available SKUs to order items and display in table
  //         this.displayAvailableSkusAsOrderItems();

  //         this.loading = false;
  //         this.cdr.detectChanges();
  //       },
  //       error: (error) => {
  //         console.error('Error loading SKUs:', error);
  //         this.error = this.translateService.instant('order.internal.messages.failed_to_load_products');
  //         this.availableSkus = [];
  //         this.loading = false;
  //         this.cdr.detectChanges();
  //       }
  //     });
  // }

  // /**
  //  * Convert available SKUs to order items and display in table
  //  */
  // private displayAvailableSkusAsOrderItems(): void {
  //   if (this.availableSkus.length === 0) {
  //     this.tableSections[0].data = [];
  //     return;
  //   }

  //   // Convert each SKU to an order item
  //   const orderItems = this.availableSkus.map((sku, index) => {
  //     const item = {
  //       id: sku.id,
  //       orderItemCode: sku.spuCode || '',
  //       skuCode: sku.skuCode || '',
  //       skuId: sku.id,
  //       skuName: sku.skuName || '',
  //       spuName: sku.spuName || sku.skuName || '',
  //       spuCode: sku.spuCode || '',
  //       quantity: 1,
  //       outerSingleSaleAmount: sku.salesPrice || 0,
  //       outerSaleAmount: sku.salesPrice || 0,
  //       innerSingleSaleAmount: sku.costPrice || 0,
  //       innerSaleAmount: sku.costPrice || 0,
  //       costPrice: sku.costPrice || 0,
  //       costTotal: (sku.costPrice || 0) * 1,
  //       giftFlag: false,
  //       spuId: sku.spuId,
  //       brandName: sku.brandName || '',
  //       categoryName: sku.categoryName || '',
  //       shipping: '',
  //       type: 'auto'
  //     };
  //     return item;
  //   });

  //   // Update table data
  //   this.tableSections[0].data = orderItems;

  //   // Update summary
  //   this.updateSummary();
  // }


  /**
   * Clear order items when store changes
   */
  private clearOrderItems(): void {
    this.tableSections[0].data = [];
    this.tableSections[0].selection = [];

    this.updateSummary();
    this.cdr.detectChanges();
  }


  private setDefaultValues() {
    const now = new Date();

    // Set default values for form fields (use Date objects — string dates
    // make AdvancedDatepicker re-normalize and re-emit every CD, freezing the page).
    const defaultValues: { [key: string]: any } = {
      orderTime: now,
      payTime: now,
      payStatus: 0,
      actionType: 'CREATE',
      businessType: 'POS',
      payAmount: 0,
      goodsTotalAmount: 0,
      costTotalAmount: 0,
      storeName: '',
      storeCode: '',
      includesShippingFee: false,
      hasThirdPartyDiscount: false,
      takingDeliveryWay: O2O_TAKING_DELIVERY_WAY.pickup,
    };

    // Initialize formData with default values
    this.formData = { ...defaultValues };

    // Update form fields with default values
    this.formFields.forEach(field => {
      if (defaultValues[field.controlName] !== undefined) {
        field.defaultValue = defaultValues[field.controlName];
      }
    });
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'save':
        this.onCreateOrder(event.data);
        break;
      case 'cancel':
        this.onCancel();
        break;
      case 'takingDeliveryWayChange':
        if (event.data?.takingDeliveryWay !== undefined) {
          this.formData.takingDeliveryWay = event.data.takingDeliveryWay;
          this.addFormComponent?.updateTakingDeliveryWay(
            event.data.takingDeliveryWay,
          );
        }
        break;
      case 'formValueChange':
        if (event.data) {
          Object.assign(this.formData, event.data);
          this.onFormValueChanges(event.data);
        }
        break;
      default:
        break;
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    switch (event.action) {
      case 'addProduct':
        this.addProduct();
        break;
      // case 'addPackage':
      //   this.addPackage();
      //   break;
      case 'deleteSelected':
        this.deleteSelectedItems(event.data || []);
        break;
      case 'selectionChange':
        // Update the table selection
        this.tableSections[0].selection = event.data || [];
        this.updateTableActions(event.data || []);
        this.updateSummary(); // Update summary when selection changes
        this.cdr.detectChanges();
        break;
      case 'cellValueChange':
        // Handle quantity or price changes
        this.onTableValueChange(event.data);
        break;
      default:
        break;
    }
  }

  /**
   * Add product via selection dialog
   */
  private addProduct() {
    this.loadProductsForDialog();
    this.showProductSelectionDialog = true;
  }

  /**
   * Load products for the dialog
   */
  private loadProductsForDialog(searchParams?: Record<string, string>) {
    this.productDialogLoading = true;
    
    const params: ProductSearchParams = {
      page: this.productDialogCurrentPage,
      size: this.productDialogPageSize,
      ...(searchParams?.['spuName'] && { spuName: searchParams['spuName'] }),
      ...(searchParams?.['spuCode'] && { spuCode: searchParams['spuCode'] }),
      ...(searchParams?.['skuCode'] && { skuCode: searchParams['skuCode'] }),
      sortBy: PRODUCT_SERVICE_CONFIG.defaultSortBy,
      ascending: PRODUCT_SERVICE_CONFIG.defaultAscending
    };

    this.productService.searchProducts(params).subscribe({
      next: (result) => {
        // Mark products as disabled if they're already selected
        this.productDialogProducts = result.products.map((product: any) => ({
          ...product,
          disable: this.alreadySelectedSkuCodes.has(product.skuCode) ? 1 : 0
        }));
        this.productDialogTotalRecords = result.totalRecords;
        this.productDialogLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading products for dialog:', error);
        this.productDialogProducts = [];
        this.productDialogTotalRecords = 0;
        this.productDialogLoading = false;
        this.cdr.detectChanges();
      }
    });
  }


  /**
   * Handle form value changes, especially store selection
   */
  onFormValueChanges(formData: any): void {
    // Check if storeId has changed
    if (formData.storeId !== undefined && formData.storeId !== this.previousStoreId) {
      this.previousStoreId = formData.storeId;
      this.onStoreChange(formData.storeId);
    }
  }



  /**
   * Update item totals when quantity or unit price changes
   */
  private updateItemTotals(item: any): void {
    const quantity = item.quantity || 1;
    const costPrice = item.costPrice || 0;
    const outerSingleSaleAmount = item.outerSingleSaleAmount || 0;

    // Calculate totals
    if (outerSingleSaleAmount) {
      item.outerSaleAmount = quantity * outerSingleSaleAmount;
    }
    if (costPrice) {
      item.innerSaleAmount = quantity * costPrice;
      item.costTotal = quantity * costPrice;
    }
    
    // Update the item in the array
    const itemIndex = this.tableSections[0].data.findIndex((tableItem: any) => tableItem.id === item.id);
    if (itemIndex !== -1) {
      this.tableSections[0].data[itemIndex] = { ...item };
    }
  }


  private deleteSelectedItems(selectedItems: any[]) {
    // Remove selected items from the table data
    this.tableSections[0].data = this.tableSections[0].data.filter(
      item => !selectedItems.some(selected => selected.id === item.id)
    );
    // Clear selection after deletion
    this.tableSections[0].selection = [];
    
    // Create new reference to trigger change detection
    this.tableSections = [...this.tableSections];
    
    // Update already selected SKU codes
    this.updateAlreadySelectedSkuCodes();
    
    this.updateTableActions([]);
    this.updateSummary();
    this.cdr.detectChanges();
  }

  private updateTableActions(selection: any[]) {
    const deleteAction = this.tableSections[0].actions?.find(a => a.action === 'deleteSelected');

    if (deleteAction) {
      deleteAction.disabled = selection.length === 0;
    }
  }

  private updateSummary() {
    // Use all table items for summary calculation (not just selected ones)
    const allItems = this.tableSections[0].data;
    // Calculate totals from all items in the table
    const totalItems = allItems.length;
    const goodsTotalAmount = allItems.reduce((sum, item) => sum + (item.innerSaleAmount || 0), 0);
    const costTotalAmount = allItems.reduce((sum, item) => sum + (item.outerSaleAmount || 0), 0);

    this.summarySection.items[0].value = totalItems; // Show total items count
    this.summarySection.items[1].value = goodsTotalAmount; // Cost total amount
    this.summarySection.items[2].value = costTotalAmount; // Pay amount equals goods total
    this.summarySection.items[3].value = costTotalAmount;

    // Mutate formData in place — replacing the object retriggers layout ngOnChanges.
    if (!this.formData || typeof this.formData !== 'object') {
      this.formData = {};
    }
    this.formData.goodsTotalAmount = goodsTotalAmount;
    this.formData.costTotalAmount = costTotalAmount;
    this.formData.payAmount = costTotalAmount;
  }

  private async onCreateOrder(formData: any) {
    this.loading = true;
    this.error = null;

    // Get all table items (not just selected ones)
    const allItems = this.tableSections[0].data || [];

    // Validate that there are items in the table
    if (allItems.length === 0) {
      this.error = this.translateService.instant('order.internal.form.validation.select_products_required');
      this.loading = false;
      return;
    }

    const invalidItems = allItems.filter(item => !item.skuId || !item.skuCode);

    if (invalidItems.length > 0) {
      this.error = this.translateService.instant('order.internal.form.validation.select_sku_required');
      this.loading = false;
      return;
    }

    try {
      // Transform form data to API format
      const orderData: OrderTableDTO = {
        orderBaseInfoDTO: {
          warehouseId: this.selectedStore?.physicalWarehouseId || 0,
          channel: formData.channel,
          storeId: formData.storeId,
          storeCode: this.selectedStore?.label || '',
          storeName: this.selectedStore?.label || '',
          outerOrderNo: formData.outerOrderNo,
          businessType: 'POS',
          orderTime: formData.orderTime,
          payStatus: formData.payStatus,
          orderRemarks: formData.orderRemarks || '',
          actionType: 'CREATE',
          copyOrigin: '',
          takingDeliveryStoreId: undefined,
          takingDeliveryStoreName: '',
          salesEmployeeRealName: '',
          takingDeliveryWay:
            formData.takingDeliveryWay || O2O_TAKING_DELIVERY_WAY.pickup,
          salesEmployeeId: undefined
        },
        orderBuyerInfoDTO: {
          buyerNick: formData.buyerNick,
          receiverName: formData.receiverName,
          mobile: formData.mobile || '',
          phone: formData.phone || '',
          provinceCode: '',
          province: this.getProvinceName(formData.provinceId) || '',
          cityCode: '',
          city: formData.city || '',
          districtCode: '',
          district: formData.district || '',
          town: formData.town || '',
          wardCode: '',
          ward: '',
          address: formData.address,
          buyerRemarks: formData.buyerRemarks || ''
        },
        orderPaymentInfoDTO: {
          payType: formData.payType,
          payAmount: formData.payAmount,
          goodsTotalAmount: formData.goodsTotalAmount,
          costTotalAmount: formData.costTotalAmount,
          payTime: formData.payTime
        },
        orderItemDTOList: allItems.map((item, index) => {
          const mappedItem = {
            orderItemCode: item.orderItemCode || `ITEM-${Date.now()}-${index + 1}`,
            skuId: item.skuId,
            skuCode: item.skuCode,
            quantity: item.quantity,
            outerSaleAmount: item.outerSaleAmount,
            outerSingleSaleAmount: item.outerSingleSaleAmount,
            innerSaleAmount: item.innerSaleAmount,
            innerSingleSaleAmount: item.innerSingleSaleAmount,
            outBalancePrice: 0,
            innerBalancePrice: 0,
            setMealFlag: item.setFlag ?? 0,
            giftFlag: item.giftFlag ? 1 : 0,
            mainOrderItemCode: "",
            transPrice: 0,
            systemPickFlag: true,
            activityId: 1
          };
          return mappedItem;
        })
      };

      const result = await this.orderService.createHandOrder(orderData).toPromise();

      if (result) {
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant('order.internal.messages.order_created_successfully')
        });

        this.skipCanDeactivate = true;
        this.addFormComponent?.form?.markAsPristine();

        setTimeout(() => {
          this.router.navigate(['/order/internal']);
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error creating order:', err);
      this.error = err?.message || this.translateService.instant('order.internal.messages.failed_to_create_order');
      
      // Show error toast
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.error || this.translateService.instant('order.internal.messages.failed_to_create_order')
      });
    } finally {
      this.loading = false;
    }
  }

  onCancel(): void {
    this.resetComponent();
    this.router.navigate(['/order/internal']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  /**
   * Reset component state to initial values (for cancel action)
   */
  private resetComponent(): void {
    this.resetComponentState();
    this.updateSummary();
    this.updateTableActions([]);
    this.cdr.detectChanges();
  }

  /**
   * Reset component state without setting defaults (for initialization)
   */
  private resetComponentState(): void {
    // Reset table data and selection
    this.tableSections[0].data = [];
    this.tableSections[0].selection = [];
    
    // Reset form data
    this.formData = {};
    
    // Reset already selected SKU codes
    this.alreadySelectedSkuCodes.clear();
    
    // Reset product dialog state
    this.showProductSelectionDialog = false;
    this.productDialogProducts = [];
    this.productDialogTotalRecords = 0;
    this.productDialogCurrentPage = 1;
    
    // Reset error state
    this.error = null;
    
    // Reset available SKUs
    this.availableSkus = [];
    this.previousStoreId = undefined;
    
    // Reset form component if available
    if (this.addFormComponent && this.addFormComponent.form) {
      this.addFormComponent.form.reset();
    }
  }

  /**
   * Handle product selection confirmation
   */
  onProductSelectionConfirm(selectedProducts: ProductItem[]): void {
    if (selectedProducts.length === 0) {
      return;
    }
    // Convert selected products to order items
    const newOrderItems = selectedProducts.map((product, index) => (
      {
      id: Date.now() + index,
      orderItemCode: `ITEM-${Date.now()}-${index + 1}`,
      skuCode: product.skuCode,
      skuId: product.skuId,
      skuName: product.skuName,
      spuName: product.spuName,
      spuCode: product.spuCode,
      quantity: 1,
      outerSingleSaleAmount: product.displayPrice || product.salesPrice || 0,
      outerSaleAmount: product.displayPrice || product.salesPrice || 0,
      innerSingleSaleAmount: product.salesPrice || 0,
      innerSaleAmount: product.salesPrice || 0,
      costPrice: product.salesPrice || 0,
      costTotal: product.displayPrice || 0,
      giftFlag: false,
      spuId: product.spuId,
      brandName: product.brandName,
      categoryName: product.categoryName,
      setFlag: product.setFlag ?? 0,
      isPackage: product.isPackage ?? product.setFlag === 1,
      fulfillmentMode: product.fulfillmentMode,
      shipping: 0,
      type: 'product'
    }));

    // Add new items to table - create new reference to trigger change detection
    this.tableSections[0].data = [...this.tableSections[0].data, ...newOrderItems];
    
    // Create a completely new tableSections array to force Angular change detection
    this.tableSections = [...this.tableSections];

    // Update already selected SKU codes
    this.updateAlreadySelectedSkuCodes();

    // Update table actions and summary (no auto-selection)
    this.updateTableActions([]);
    this.updateSummary();
    this.error = null;
    this.cdr.detectChanges();
  }

  /**
   * Handle product selection dialog visibility change
   */
  onProductSelectionDialogVisibleChange(visible: boolean): void {
    this.showProductSelectionDialog = visible;
    if (!visible) {
      // Reset dialog state when closed
      this.productDialogProducts = [];
      this.productDialogTotalRecords = 0;
      this.productDialogCurrentPage = 1;
    }
  }

  /**
   * Handle product search in dialog
   */
  onProductSearch(searchParams: Record<string, string>): void {
    this.productDialogCurrentPage = 1;
    this.loadProductsForDialog(searchParams);
  }

  /**
   * Autocomplete search on the product table toolbar (same UX as O2O create).
   */
  onProductSearchField(event: { query: string }): void {
    if (!event?.query || event.query.trim().length < 1) {
      this.addFormComponent?.updateProductSearchSuggestions([]);
      return;
    }

    const params: ProductSearchParams = {
      page: 1,
      size: 10,
      skuName: event.query.trim(),
      sortBy: PRODUCT_SERVICE_CONFIG.defaultSortBy,
      ascending: PRODUCT_SERVICE_CONFIG.defaultAscending,
    };

    this.productService.searchProducts(params).subscribe({
      next: (result) => {
        const existingSkuCodes = new Set(
          this.tableSections[0].data.map((item: any) => item.skuCode),
        );
        const availableProducts = result.products
          .filter((product: any) => !existingSkuCodes.has(product.skuCode))
          .map((product: any) => ({
            ...product,
            disable: this.alreadySelectedSkuCodes.has(product.skuCode) ? 1 : 0,
          }));
        this.addFormComponent?.updateProductSearchSuggestions(availableProducts);
      },
      error: (error) => {
        console.error('Error searching products:', error);
        this.addFormComponent?.updateProductSearchSuggestions([]);
      },
    });
  }

  /**
   * Select a product from the autocomplete search field.
   */
  onProductSearchSelect(event: { product: any }): void {
    if (!event?.product) {
      return;
    }

    const product = event.product.value || event.product;
    const productItem: ProductItem = {
      ...product,
      disable: this.alreadySelectedSkuCodes.has(product.skuCode) ? 1 : 0,
    };

    this.onProductSelectionConfirm([productItem]);
  }

  /**
   * Handle product search reset in dialog
   */
  onProductSearchReset(): void {
    this.productDialogCurrentPage = 1;
    this.loadProductsForDialog();
  }

  /**
   * Handle product page change in dialog
   */
  onProductPageChange(page: number): void {
    this.productDialogCurrentPage = page;
    this.loadProductsForDialog();
  }

  /**
   * Update the set of already selected SKU codes based on current table data
   */
  private updateAlreadySelectedSkuCodes(): void {
    this.alreadySelectedSkuCodes.clear();
    this.tableSections[0].data.forEach((item: any) => {
      if (item.skuCode) {
        this.alreadySelectedSkuCodes.add(item.skuCode);
      }
    });
  }

  /**
   * Handle table value changes (quantity, price, etc.)
   */
  onTableValueChange(data: any): void {
    if (!data || !data.rowData) return;

    const item = data.rowData;
    const field = data.prop;
    const value = data.value;
    
    // Find the item in the table data
    const itemIndex = this.tableSections[0].data.findIndex((tableItem: any) => tableItem.id === item.id);
    if (itemIndex === -1) return;

    // Update the item
    this.tableSections[0].data[itemIndex] = {
      ...this.tableSections[0].data[itemIndex],
      [field]: value
    };

    // Recalculate totals based on the changed field
    this.updateItemTotals(this.tableSections[0].data[itemIndex]);
    // Update summary
    this.updateSummary();

    // Force change detection
    this.cdr.detectChanges();
  }
}


